import AddLinkIcon from '@mui/icons-material/AddLink';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import { ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useContext, useCallback } from 'react';
import { useEditorBubbleMenu } from '../../context';
import { AssetViewContext } from 'screens/editorial/asset/context/AssetViewContextProvider';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { buildViewAtSiteUrl } from 'utility/functions';
import { EditorialAssetSearchResultItem } from 'utility/types/dataProvider';
import { ARTICLE_BODY_COPY_ID } from 'screens/editorial/asset/utility/getEditorConfigs';

export const LinkBubbleList = () => {
  const { t } = useTranslation();
  const { editor, setBubbleView, bubbleCleanup } = useEditorBubbleMenu();
  const assetViewContext = useContext(AssetViewContext);
  const { activeSite } = useCosmos();

  // Because richtext generic editor setup has the link bubble but also threads
  // we dont want threads to show up in places like user edit etc where
  // link is needed but not commenting threads and only show the threads on articles
  const element = editor?.options?.element;
  const isEditorialAndArticleContent =
    element && typeof element === 'object' && 'id' in element
      ? element.id === ARTICLE_BODY_COPY_ID
      : false;

  const handleUseAsset = useCallback(
    async (items: EditorialAssetSearchResultItem[]) => {
      if (!editor) {
        return;
      }

      const selectedItem = items?.[items.length - 1];
      if (!selectedItem?.uuid) {
        return;
      }

      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({
          href: buildViewAtSiteUrl(
            activeSite,
            selectedItem.uuid,
            selectedItem.properties?.app || 'editorial',
          ),
          target: '_self',
        })
        .run();

      bubbleCleanup();
    },
    [editor, activeSite, bubbleCleanup],
  );

  const handleLinkToUrlClick = () => {
    setBubbleView('linkEditor');
  };

  const handleLinkToAssetClick = () => {
    if (!editor) {
      return;
    }

    bubbleCleanup();
    assetViewContext?.openRelationshipsSearchModal({
      onUseAsset: handleUseAsset,
      selectionMode: 'single',
      footerActionMode: 'addOnly',
    });
  };

  if (!editor) return null;

  return (
    <>
      <ListItem disablePadding>
        <ListItemButton
          data-testid="bubble-menu-link-to-url-btn"
          onClick={handleLinkToUrlClick}
        >
          <ListItemIcon>
            <AddLinkIcon />
          </ListItemIcon>
          <ListItemText primary={t('blox.editorial.toolbar.link_to_url')} />
        </ListItemButton>
      </ListItem>
      {isEditorialAndArticleContent && (
        <ListItem disablePadding>
          <ListItemButton
            data-testid="bubble-menu-link-to-asset-btn"
            onClick={handleLinkToAssetClick}
          >
            <ListItemIcon>
              <CollectionsBookmarkIcon />
            </ListItemIcon>
            <ListItemText primary={t('blox.editorial.toolbar.link_to_asset')} />
          </ListItemButton>
        </ListItem>
      )}
    </>
  );
};
